import React from 'react';
import { connect } from 'react-redux';
import { Grid, Button, Paper, Typography, Box, List, Divider, Container, Collapse } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import BackIcon from '@mui/icons-material/ArrowBackIosRounded';
import SelectableExportListItem from './SelectableExportListItem';
import ExportViewer from './ExportViewer';
import isEmpty from 'lodash.isempty';
import videoActions from '../VideosPage/store/actions';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { collectionHelper } from '../../../utilities/basicUtilities';
import get from 'lodash.get';
import CircleProgressButton from '../../buttons/CircleProgressButton';
import TimeAgo from 'react-timeago';
import { exportsActions } from './store';
import ExportActionsPanel from './ExportActionsPanel';
import MobileExportActionsPanel from './MobileExportActionsPanel';
import { isRemoveWatermarkPossible } from './ExportsPageHelpers';
import { appOperations } from '../../../App/store';
import ColoredSnackbarConstants from '../../misc/ColoredSnackbar/ColoredSnackbarConstants';
import { MediaQuery } from '../../../constants/MediaQuerySizes';
import withRouter from '../../../App/withRouter';
import { EXPORT_STATUSES } from '../../../constants/ExportStatuses';
import { isPresetsManager } from '../../../utilities/userUtilities';
import ManageVideoPresetDialog from '../../dialogs/ManageVideoPresetDialog/ManageVideoPresetDialog';

const leftPanelWidth = 320;
const topBarHeight = 80;
const actionButtonHeight = 66;

const youTubeFailureRoute = 'ytf';

const styles = (theme) => ({
    main: {
        position: 'absolute',
        top: 0, bottom: 0, right: 0, left: 0,
        [theme.breakpoints.down('xl')]: {
            overflowY: 'scroll'
        }
    },
    topBarContainer: {
        height: theme.spacing(8),
        borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        padding: '0 ' + theme.spacing(2).toString(),
        marginBottom: theme.spacing(1)
    },
    topBarItemsWrapper: {
        height: '100%',
        [theme.breakpoints.down(768)]: {
            flexWrap: 'nowrap'
        }
    },
    filesListContainer: {
        overflow: 'hidden'
    },
    actionsContainer: {
        padding: theme.spacing(2)
    },
    projectNameContainer: {
        [theme.breakpoints.down(768)]: {
            width: '100%',
            textAlign: 'end',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
        }
    },
    projectNameLabel: {
        marginLeft: theme.spacing(4),
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        [theme.breakpoints.down(500)]: {
            textAlign: "end",
            fontSize: '0.8rem',
        }
    },
    exportTimeLabel: {
        textAlign: 'right',
        [theme.breakpoints.down(768)]: {
            display: 'none'
        }
    },
    exportsList: {
        paddingTop: theme.spacing(0),
        paddingBottom: theme.spacing(0),
    },
    listToggleButton: {
        borderRadius: '0px',
        textTransform: 'none',
        color: theme.palette.text.secondary
    },
    leftPanelContainer: {
        position: 'absolute',
        top: theme.spacing(8),
        bottom: theme.spacing(8),
        left: 0,
        width: leftPanelWidth,
        padding: 0,
        [theme.breakpoints.down(MediaQuery.MIN_DESKTOP)]: {
            display: 'none'
        }
    },
    mobileContainer: {
        width: "95%",
        margin: '0 auto',
        marginTop: '30px',
        marginBottom: '100px',
        [theme.breakpoints.up(MediaQuery.MAX_MOBILE)]: {
            display: 'none'
        }
    },
    viewerContainer: {
        height: 'calc(100% - ' + (theme.spacing(8) * 2 + 1).toString() + 'px)',
        width: 'calc(100% - ' + leftPanelWidth + 'px)',
        maxWidth: 'none',
        padding: 0,
        marginRight: 0,
        position: 'relative',
        [theme.breakpoints.down(MediaQuery.MIN_DESKTOP)]: {
            width: '100%',
            height: 'auto'
        }
    },
});

class ExportsPage extends React.Component {
    render() {
        const {
            classes, projectName, videoExports, selectedExport, accountDetails, setProperty, manageVideoPresetDialogOpen
        } = this.props;

        // screen height - (top bar + all actions buttons + additional margins)
        const removeWatermarkPossible = isRemoveWatermarkPossible(accountDetails, videoExports);
        var exportsListMaxHeight = `calc(100vh - ${topBarHeight + (actionButtonHeight * (removeWatermarkPossible ? 4 : 3)) + 93}px)`;
        const isUserPresetsManager = isPresetsManager(accountDetails.roles);

        return (
            <Box className={classes.main}>
                <Paper square elevation={0} className={classes.topBarContainer}>
                    <Grid
                        container
                        direction='row'
                        alignItems='center'
                        className={classes.topBarItemsWrapper}
                    >
                        <Grid>
                            <Button startIcon={<BackIcon />} onClick={this.onBackToVideosClick}>Videos</Button>
                        </Grid>
                        <Grid size={{ xs: 9, md: 9 }} className={classes.projectNameContainer}>
                            <Typography align="left" variant='subtitle1' color='textSecondary' className={classes.projectNameLabel}>
                                {projectName}
                            </Typography>
                        </Grid>
                        <Grid sx={{ flexGrow: 1 }}>
                            {!isEmpty(get(selectedExport, 'exportEndedTime')) &&
                                <Typography variant='subtitle1' color='textSecondary' className={classes.exportTimeLabel}>
                                    Created <TimeAgo date={selectedExport.exportEndedTime} title={new Date(selectedExport.exportEndedTime).toString()} />
                                </Typography>
                            }
                        </Grid>
                    </Grid>
                </Paper>
                <Container disableGutters className={classes.leftPanelContainer}>
                    <Box className={classes.actionsContainer}>
                        <Grid
                            container
                            direction='column'
                            justifyContent='center'
                            alignItems='stretch'
                            spacing={2}
                        >
                            {/* Render action buttons */}
                            <ExportActionsPanel />
                            {this.renderExportsList(exportsListMaxHeight)}
                        </Grid>
                        <Grid />
                        {isUserPresetsManager && <Grid>
                            <Button
                                fullWidth
                                style={{ marginTop: "15px" }}
                                variant='outlined'
                                color='secondary'
                                onClick={() => setProperty("manageVideoPresetDialogOpen", true)}
                            >
                                Preset actions
                            </Button>
                        </Grid>}
                    </Box>
                </Container>
                <Container maxWidth={false} className={classes.viewerContainer}>
                    <ExportViewer />
                </Container>
                <Box className={classes.mobileContainer}>
                    <Grid
                        container
                        direction='column'
                        justifyContent='center'
                        alignItems='stretch'
                        spacing={2}
                    >
                        {/* Render action buttons */}
                        <MobileExportActionsPanel />
                        {this.renderExportsList(exportsListMaxHeight)}
                    </Grid>
                    {isUserPresetsManager && <Grid>
                        <Button
                            fullWidth
                            style={{ marginTop: "15px" }}
                            variant='outlined'
                            color='secondary'
                            onClick={() => setProperty("manageVideoPresetDialogOpen", true)}
                        >
                            Preset actions
                        </Button>
                    </Grid>
                    }
                </Box>
                <ManageVideoPresetDialog
                    open={manageVideoPresetDialogOpen}
                    onClose={() => setProperty("manageVideoPresetDialogOpen", false)}
                />
            </Box>
        );
    }

    renderVideoExportListItem = (videoExportData) => {
        const { selectedExport, isListCollapsed } = this.props;

        return <SelectableExportListItem
            key={videoExportData.exportId}
            isSelected={videoExportData.exportId === get(selectedExport, 'exportId')}
            isDisabled={isListCollapsed}
            videoExportData={videoExportData}
            onDelete={this.onExportItemDelete}
            onSelect={this.onExportItemSelect}
        />
    }

    renderExportsList = (exportsListMaxHeight) => {
        const {
            classes, videoExports, isExportsDataLoading,
            isListCollapsed
        } = this.props;

        return (
            <Grid>
                <Paper className={classes.filesListContainer}>
                    {!isEmpty(videoExports) &&
                        <Collapse in={!isListCollapsed} timeout='auto' unmountOnExit>
                            <OverlayScrollbarsComponent style={{ maxHeight: exportsListMaxHeight, minHeight: '68px' }}>
                                <List dense className={classes.exportsList}>
                                    {videoExports.filter(x => x.status === EXPORT_STATUSES.RUNNING).map((videoExport) => this.renderVideoExportListItem(videoExport))}
                                    {videoExports.filter(x => x.status === EXPORT_STATUSES.PENDING).sort((a, b) => a.queuePosition - b.queuePosition).map((videoExport) => this.renderVideoExportListItem(videoExport))}
                                    {videoExports.filter(x => x.status !== EXPORT_STATUSES.PENDING && x.status !== EXPORT_STATUSES.RUNNING).map((videoExport) => this.renderVideoExportListItem(videoExport))}
                                </List>
                            </OverlayScrollbarsComponent>
                            <Divider />
                        </Collapse>
                    }

                    <CircleProgressButton
                        className={classes.listToggleButton}
                        fullWidth={true}
                        loading={isExportsDataLoading || isEmpty(videoExports)}
                        onClick={this.onListToggle}
                    >
                        {isExportsDataLoading || isEmpty(videoExports) ?
                            'loading exports...'
                            :
                            isListCollapsed ? `Show ${videoExports.length} export${videoExports.length !== 1 ? 's' : ''}` : 'Show less'
                        }
                    </CircleProgressButton>
                </Paper>
            </Grid>
        )
    }

    componentDidMount() {
        const { params, navigate, loadVideoExports, setProperty, openSnack } = this.props;

        loadVideoExports(params.videoId, (videoExports) => {
            if (!isEmpty(videoExports)) {
                // Set the item as an active export
                var selectedExport = undefined;
                var exportId = params.exportId;

                if (!isEmpty(exportId)) {
                    selectedExport = collectionHelper(videoExports).firstOrDefault((ve) => ve.exportId === exportId);
                }
                if (isEmpty(selectedExport)) {
                    selectedExport = collectionHelper(videoExports).firstOrDefault();
                }

                setProperty('selectedExport', selectedExport);
            }
        });

        if (params.exportId === youTubeFailureRoute) {
            openSnack('Failed to connect YouTube account.', ColoredSnackbarConstants.SNACKBAR_ERROR);
            navigate(`/exports/${params.videoId}`, { replace: true });
        }
    }

    componentWillUnmount() {
        const { setProperty } = this.props;
        setProperty('projectName', undefined);
        setProperty('selectedExport', undefined);
        setProperty('isListCollapsed', true);
    }

    onBackToVideosClick = () => {
        this.props.navigate('/videos');
    };

    onExportItemDelete = (exportData) => {
        const { deleteVideoExport, refreshDataOnVideosPage, navigate } = this.props;

        if (!exportData.isDeleting) {
            deleteVideoExport(exportData, () => {
                refreshDataOnVideosPage();
            }, navigate);
        }
    };

    onListToggle = () => {
        const { isListCollapsed, setProperty } = this.props;
        setProperty('isListCollapsed', !isListCollapsed);
    }

    onExportItemSelect = (exportData) => {
        const { selectedExport, setProperty } = this.props;

        if (exportData.exportId !== selectedExport.exportId) {
            // Set the active export object
            setProperty('selectedExport', exportData);
        }
    }
}

const mapStateToProps = (state) => {
    const {
        isExportsDataLoading,
        manageVideoPresetDialogOpen,
        projectName,
        videoExports,
        selectedExport,
        isListCollapsed
    } = state.exports;
    const {
        accountDetails
    } = state.app;

    return {
        isExportsDataLoading,
        manageVideoPresetDialogOpen,
        projectName,
        videoExports,
        selectedExport,
        accountDetails,
        isListCollapsed
    };
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(exportsActions.setProperty(key, value));
    const loadVideoExports = (videoId, callback) => dispatch(exportsActions.loadVideoExports(videoId, callback));
    const deleteVideoExport = (exportData, callback, navigate) => dispatch(exportsActions.deleteVideoExport(exportData, callback, navigate));

    const refreshDataOnVideosPage = () => dispatch(videoActions.setVideosFresh(false));
    const openSnack = (text, variant) => dispatch(appOperations.openSnack(text, variant));

    return {
        setProperty,
        loadVideoExports,
        deleteVideoExport,
        refreshDataOnVideosPage,
        openSnack
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withStyles(ExportsPage, styles)));